import {
  FIREBASE_DEPRECATION_TOOLTIP_CONTENT,
  FIREBASE_JSON_TYPE_LABEL,
  LEGACY_FIREBASE_TYPE_LABEL,
} from 'modules/webPush/WebPushV2/constants';
import { FieldErrors, FieldValues } from 'react-hook-form';
import { ActionMenu } from 'ui/actionMenu';
import { AllowedActionMenuElements, MENU_ALIGNMENTS } from 'ui/actionMenu/types';
import { BUTTON_SIZES, BUTTON_VARIANTS, IconButton } from 'ui/button';
import { ClickToCopyCode } from 'ui/clickToCopy';
import { Delete16, Overflow16 } from 'ui/icons';
import { Tooltip } from 'ui/tooltip';
import {
  CreateWebPushFormValues,
  WebPushAuthType,
  WebPushIntegration,
  WebPushPlatform,
} from '../types';
import {
  ActionButtonContainer,
  BossRow,
  IntegrationTableButton,
  StyledWarningIcon,
} from './components/styles';
import { WebPushFormValues, useModalsContext, useWebPushMetadataContext } from './context';
import { IntegrationTableRow, ModalType, OpenedModal } from './types';

/** Returns a formatted integration object for create web push integration API request. */
export const formatIntegrationFieldsForSave = async (
  fields: WebPushFormValues,
): Promise<CreateWebPushFormValues> => {
  switch (fields.authType) {
    case WebPushAuthType.FIREBASE_JSON: {
      const { firebaseJsonFile, ...rest } = fields;
      const fcmJson = (await firebaseJsonFile?.text()) || '';
      return { ...rest, fcmJson, platform: WebPushPlatform.FCM };
    }
    default:
      return { ...fields, platform: WebPushPlatform.FCM };
  }
};

export const getIsModalOpen = (openedModals: OpenedModal[], modalType: ModalType) =>
  !!openedModals?.find((openedModal) => openedModal.modalType === modalType);

export const formatIntegrationTypeLabel = (authType: WebPushAuthType | undefined) =>
  authType === WebPushAuthType.FIREBASE_JSON
    ? FIREBASE_JSON_TYPE_LABEL
    : LEGACY_FIREBASE_TYPE_LABEL;

export const useCreateIntegrationTableRow = (): IntegrationTableRow => {
  const { openModal } = useModalsContext();
  const { integration, isLegacyIntegration } = useWebPushMetadataContext();

  const handleOpenJsonFormModal = () => {
    openModal(ModalType.ConfigureIntegrationModal);
  };

  const handleOpenTestWebPushModal = (selectedIntegration?: WebPushIntegration) => {
    openModal(ModalType.TestWebPushModal, {
      isTestingLater: true,
      integration: selectedIntegration,
    });
  };

  const handleOpenDeleteModal = () => {
    openModal(ModalType.DeleteIntegrationModal);
  };

  const editOption = {
    as: AllowedActionMenuElements.Button,
    content: 'Edit integration',
    id: 'edit-integration-option',
    onClick: handleOpenJsonFormModal,
  };

  const deleteOption = {
    as: AllowedActionMenuElements.Button,
    content: <BossRow>Delete</BossRow>,
    id: 'delete-integration-option',
    onClick: handleOpenDeleteModal,
  };

  const actionMenuOptions = [...(!isLegacyIntegration ? [editOption] : []), deleteOption];

  const integrationRow = {
    type: formatIntegrationTypeLabel(integration?.authType),
    serverKey: (
      <ClickToCopyCode content={integration?.fcmServerKey || ''}>
        {`${integration?.fcmServerKey?.substring(0, 30)}...`}
      </ClickToCopyCode>
    ),
    actions: (
      <ActionButtonContainer>
        {isLegacyIntegration && (
          <Tooltip tooltipContent={FIREBASE_DEPRECATION_TOOLTIP_CONTENT}>
            <IntegrationTableButton
              data-test="web-push-integration-table-legacy-update-button"
              onClick={handleOpenJsonFormModal}
              size={BUTTON_SIZES.SMALL}
            >
              <StyledWarningIcon />
              Update
            </IntegrationTableButton>
          </Tooltip>
        )}
        <IntegrationTableButton
          data-test="web-push-integration-table-test-push-button"
          onClick={() => handleOpenTestWebPushModal(integration)}
          size={BUTTON_SIZES.SMALL}
        >
          Test push
        </IntegrationTableButton>
        {isLegacyIntegration ? (
          <IconButton
            data-test="web-push-integration-table-delete-button"
            onClick={handleOpenDeleteModal}
            size={BUTTON_SIZES.SMALL}
            style={{ padding: 0 }}
            variant={BUTTON_VARIANTS.SECONDARYDESTRUCTIVE}
          >
            <Delete16 />
          </IconButton>
        ) : (
          <ActionMenu align={MENU_ALIGNMENTS.RIGHT} options={actionMenuOptions}>
            <IconButton size={BUTTON_SIZES.SMALL} variant={BUTTON_VARIANTS.SECONDARY}>
              <Overflow16 />
            </IconButton>
          </ActionMenu>
        )}
      </ActionButtonContainer>
    ),
  };

  const emptyRow = {
    type: FIREBASE_JSON_TYPE_LABEL,
    actions: (
      <ActionButtonContainer>
        <IntegrationTableButton
          data-test="web-push-integration-table-configure-button"
          onClick={handleOpenJsonFormModal}
        >
          Configure
        </IntegrationTableButton>
      </ActionButtonContainer>
    ),
  };

  return integration ? integrationRow : emptyRow;
};

/** Returns error message strings keyed by field name. */
export const getFormErrorMessages = (errors: FieldErrors<FieldValues>) =>
  Object.entries(errors).reduce((acc: Record<string, string>, [fieldName, error]) => {
    acc[fieldName] = error?.message?.toString() || '';
    return acc;
  }, {});

/** Returns whether integration is legacy Firebase or the new Firebase JSON format. */
export const getIsLegacyWebPushIntegration = (integration?: WebPushIntegration) =>
  !!integration && integration.authType !== WebPushAuthType.FIREBASE_JSON;
